import React from "react";
import { dlog } from "../utils/debugLog";

/**
 * Tiny React error boundary that logs caught errors via `dlog` so they
 * show up in the on-screen debug panel.
 *
 * Why this exists:
 * On mobile we can't open Safari Web Inspector against the Kin webview,
 * so an exception thrown deep in the chat tree was silent — React just
 * unmounted the broken subtree and we got a white screen with no
 * indication of what blew up. Wrapping the chat root in this boundary
 * means any throw lands in the debug panel with a stack trace.
 *
 * In production we still want the user to see *something* useful, so
 * the fallback shows a short error message and a Reload button rather
 * than re-throwing.
 */

interface Props {
  /** Short label included in the dlog message and the visible fallback. */
  label: string;
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

export class DebugErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    dlog.error(
      `ErrorBoundary caught (${this.props.label}):`,
      error,
      info?.componentStack ?? "",
    );
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            fontFamily: "sans-serif",
            textAlign: "center",
            padding: "24px",
            color: "#555",
          }}
        >
          <p style={{ fontSize: "15px", maxWidth: "320px" }}>
            Something went wrong loading the chat. Please reload the page.
          </p>
          <p
            style={{
              fontSize: "11px",
              color: "#999",
              marginTop: "12px",
              maxWidth: "320px",
              wordBreak: "break-word",
            }}
          >
            {this.state.error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "16px",
              padding: "8px 16px",
              backgroundColor: "#005fff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
